import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@material-ui/core";
import PageContainer, { CardPageItem } from "../custom/Page";
import HeaderPageItem from "../custom/HeaderPageItem";
import { getDataFromServer } from "../util";
import { ChevronRight } from "@material-ui/icons";
import { Link } from "react-router-dom";
import { generateAllMonthlyUserReports } from "../actions";
import { syncGender, syncUsersWithSumsub } from "../actions/scripts";
import SendWelcomeOnboarding from "./components/AdminScripts/SendWelcomeOnboarding";

// Componente principal da página
const AdminScripts = () => (
  <PageContainer>
    <HeaderPageItem title="Scripts" showBackButton destination="/more" />
    <CardPageItem>
      <Typography variant="h6">
        Executar scripts para atualizar alguns recursos.
      </Typography>
      <Typography gutterBottom color="error">
        ATENÇÃO: só utilize os recursos abaixo se souber exatamente o que está
        fazendo!
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <UserAddressSyncCard />
        </Grid>
        <Grid item xs={12}>
          <MonthlyUserReportCard />
        </Grid>
        <Grid item xs={12}>
          <SyncUsersWithSumsubCard />
        </Grid>
        <Grid item xs={12}>
          <SendWelcomeOnboarding />
        </Grid>
      </Grid>
    </CardPageItem>
  </PageContainer>
);

// Card específico para sincronizar endereços
const UserAddressSyncCard = () => (
  <Card>
    <CardContent>
      <Typography gutterBottom color="primary">
        Sincronizar endereços dos usuários com API OPENCEP
      </Typography>
      <Typography gutterBottom variant="body2">
        syncUserWithAddressApi
      </Typography>
      <Typography gutterBottom variant="body1">
        Este script sincroniza e atualiza automaticamente os endereços dos
        usuários cadastrados, consultando a API OPENCEP a partir do CEP
        informado no cadastro de cada usuário.
      </Typography>
      <Typography gutterBottom variant="body1">
        <b>Obs.:</b> Este script pode demorar, pois depende de um serviço de
        terceiros que é gratuito e ficamos denpendendo de disponibilidade.
      </Typography>
      <Typography gutterBottom variant="body1" color="primary">
        Se houver algum CEP que não foi possível conferir, ele será apresentado
        abaixo para análise e validação manual.
      </Typography>
      <SyncExecution />
    </CardContent>
  </Card>
);

const SyncExecution = () => {
  const [status, setStatus] = useState("idle");
  const [failedUsers, setFailedUsers] = useState([]);

  const handleSync = async () => {
    setStatus("loading");
    setFailedUsers([]);
    try {
      const response = await getDataFromServer(
        "POST",
        "/user/sync-user-with-address-api",
        null,
        true,
      );
      setFailedUsers(response || []);
      setStatus("done");
    } catch (e) {
      setStatus("error");
    }
  };

  return (
    <>
      {status === "error" && (
        <Typography gutterBottom variant="body2" color="error" align="center">
          Houve um problema na execução do script.
        </Typography>
      )}

      {status !== "loading" && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-end",
            marginTop: 8,
          }}
        >
          <Button onClick={handleSync} variant="outlined">
            Executar agora
          </Button>
        </div>
      )}

      {status === "loading" && (
        <div style={{ textAlign: "center" }}>
          <CircularProgress />
        </div>
      )}

      {status === "done" && failedUsers.length > 0 && (
        <>
          <Typography variant="subtitle1" gutterBottom>
            Usuários que não foram possiveis sincronizar:
          </Typography>
          <Typography variant="body1" gutterBottom>
            Dica: Não feche esta janela, clicar nos usuários abrira a pagina de
            perfil deles para conferir e editar se necessário
          </Typography>
          <Typography variant="body1" gutterBottom></Typography>
          <List>
            {failedUsers.map((user) => (
              <ListItem
                key={user.id}
                component={Link}
                to={"/user/" + user.id}
                target="_blank"
              >
                <ListItemText
                  primary={
                    <Typography variant="body1" gutterBottom>
                      {`${user.name} - ${user.id}`}
                    </Typography>
                  }
                  secondary={
                    <Typography color="error" variant="body2">
                      {user.reason}
                    </Typography>
                  }
                />
                <ChevronRight color="primary" />
              </ListItem>
            ))}
          </List>
        </>
      )}

      {status === "done" && failedUsers.length === 0 && (
        <Typography
          variant="body2"
          color="primary"
          align="center"
          style={{ marginTop: 16 }}
        >
          Todos os endereços foram sincronizados com sucesso!
        </Typography>
      )}
    </>
  );
};

// Card específico para gerar os dados para os relatórios de usuários
const MonthlyUserReportCard = () => (
  <Card>
    <CardContent>
      <Typography gutterBottom color="primary" variant="h6">
        Geração de Relatórios Mensais de Usuários
      </Typography>
      <Typography gutterBottom variant="body2">
        generateAllMonthlyUserReports
      </Typography>
      <Typography gutterBottom variant="body1">
        Este processo gera um <i>snapshot</i> mensal com base nos usuários da plataforma,
        com o objetivo de manter um histórico atualizado dos dados.
      </Typography>
      <Typography gutterBottom variant="body1">
        <b>Importante:</b> A geração é executada automaticamente todos os dias às 3h.
        Caso já exista um relatório do mês autal, será substituído pelo mais recente.
    </Typography>
      <Typography gutterBottom variant="body1" color="primary">
        Relatórios com mais de 1 ano são removidos automaticamente para otimizar o armazenamento e o desempenho da base de dados.
      </Typography>
      <MonthlyReportCreate />
    </CardContent>
  </Card>
);

const MonthlyReportCreate = () => {
  const [status, setStatus] = useState("idle");
  const [numberOfLines, setNumberOfLines] = useState(0);

  const handleCreate = async () => {
    setStatus("loading");
    try {
      const response = await generateAllMonthlyUserReports();
      setNumberOfLines(response.rowsAffected);
      setStatus("done");
    } catch (e) {
      setStatus("error");
    }
  };

  return (
    <>
      {status === "error" && (
        <Typography gutterBottom variant="body2" color="error" align="center">
          Houve um problema na geração dos relatórios mensais.
        </Typography>
      )}

      {status !== "loading" && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "flex-end",
            marginTop: 8,
          }}
        >
          <Button onClick={handleCreate} variant="outlined">
            Executar
          </Button>
        </div>
      )}

      {status === "loading" && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <CircularProgress />
        </div>
      )}

      {status === "done" && (
        <>
          <Typography
            variant="body2"
            color="primary"
            align="center"
            style={{ marginTop: 16 }}
          >
            Todos os relatórios mensais foram gerados com sucesso!
          </Typography>
          <Typography
            variant="body2"
            color="primary"
            align="center"
            style={{ marginTop: 16 }}
          >
            {numberOfLines} registros criados
          </Typography>
        </>
      )}
    </>
  );
};

const SyncUsersWithSumsubCard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [failedUsers, setFailedUsers] = useState([]);

  const handleSync = async () => {
    setIsLoading(true);
    setHasError(false);
    setFailedUsers([]);
    try {
      const userIds = await syncUsersWithSumsub();
      setFailedUsers(userIds || []);
    } catch (e) {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography gutterBottom color="primary" variant="h6">
          Sincronizar os usuários com os dados da sumsub (gênero e applicantId)
        </Typography>
        <Typography gutterBottom variant="body2">
          syncUsersWithSumsub
        </Typography>
        <Typography gutterBottom variant="body1">
          Este script sincroniza e atualiza automaticamente o gênero e o
          applicantId dos usuários validados na Sumsub.
        </Typography>
        <Typography gutterBottom variant="body1">
          <b>Obs.:</b> Este script pode demorar, pois depende de um serviço de
          terceiros e da quantidade de usuários já cadastrados na Sumsub.
        </Typography>
        <Typography gutterBottom variant="body1" color="primary">
          Se houver algum usuário que não foi possível conferir, ele será
          apresentado abaixo para análise e validação manual. Pode ser
          atualizado diretamente na Sumsub, no qual o Webhook enviará a
          atualização automaticamente para a nossa plataforma.
        </Typography>
        {!hasError && !!failedUsers.length && (
          <Box style={{ marginTop: "1em" }}>
            <Typography variant="body1" style={{ fontWeight: "bold" }}>
              Usuários não encontrados na Sumsub:
            </Typography>
            <Box
              style={{
                maxHeight: "15em",
                marginBottom: "1em",
                overflowY: "scroll",
              }}
            >
              <CardContent>
                {failedUsers.map(({ id, name, email }) => (
                  <Box key={id} display="flex" flexDirection="column">
                    <Typography
                      style={{ fontWeight: "bold" }}
                      variant="body1"
                      color="primary"
                    >
                      {id}
                    </Typography>
                    <Typography variant="body1">Nome: {name}</Typography>
                    <Typography gutterBottom variant="body1">
                      Email: {email}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Box>
          </Box>
        )}
        <Box width="100%" display="flex" justifyContent="flex-end">
          <Button onClick={handleSync} variant="outlined" disabled={isLoading}>
            {isLoading ? <CircularProgress /> : "Executar agora"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AdminScripts;
